import { Injectable } from '@angular/core';

@Injectable()
export class ColorGeneratorService {
  #darkText = '#070b0f';
  #white = '#ffffff';
  #black = '#000000';

  setPrimary(hexCode: string) {
    const lightText = this.colorMix(hexCode, this.#white, .95);

    const shade5 = this.colorMix(hexCode, this.#black, .90);
    const shade4 = this.colorMix(hexCode, this.#black, .60);
    const shade3 = this.colorMix(hexCode, this.#black, .50);
    const shade2 = this.colorMix(hexCode, this.#black, .30);
    const shade1 = this.colorMix(hexCode, this.#black, .20);

    const tint1 = this.colorMix(hexCode, this.#white, .40);
    const tint2 = this.colorMix(hexCode, this.#white, .60);
    const tint3 = this.colorMix(hexCode, this.#white, .80);

    const primaryStyle = `:root {
      --primary: ${hexCode};
      --primary-text: ${this.getContrastColor(hexCode, lightText, this.#darkText)};
      --primary-shade-5: ${shade5};
      --primary-shade-5-text: ${this.getContrastColor(shade5, lightText, this.#darkText)};
      --primary-shade-4: ${shade4};
      --primary-shade-4-text: ${this.getContrastColor(shade4, lightText, this.#darkText)};
      --primary-shade-3: ${shade3};
      --primary-shade-3-text: ${this.getContrastColor(shade3, lightText, this.#darkText)};
      --primary-shade-2: ${shade2};
      --primary-shade-2-text: ${this.getContrastColor(shade2, lightText, this.#darkText)};
      --primary-shade-1: ${shade1};
      --primary-shade-1-text: ${this.getContrastColor(shade1, lightText, this.#darkText)};

      --primary-tint-1: ${tint1};
      --primary-tint-1-text: ${this.getContrastColor(tint1, lightText, this.#darkText)};
      --primary-tint-2: ${tint2};
      --primary-tint-2-text: ${this.getContrastColor(tint2, lightText, this.#darkText)};
      --primary-tint-3: ${tint3};
      --primary-tint-3-text: ${this.getContrastColor(tint3, lightText, this.#darkText)};

      --light-text: ${lightText};
    }`;

    const sheet = document.createElement('style');
    sheet.innerHTML = primaryStyle;
    document.head.appendChild(sheet);
  }

  setSecondary(hexCode: string) {
    const lightText = this.colorMix(hexCode, this.#white, .95);

    const shade1 = this.colorMix(hexCode, this.#black, .20);
    const tint1 = this.colorMix(hexCode, this.#white, .40);

    const secondaryStyle = `:root {
      --secondary: ${hexCode};
      --secondary-text: ${this.getContrastColor(hexCode, lightText, this.#darkText)};
      --secondary-shade-1: ${shade1};
      --secondary-shade-1-text: ${this.getContrastColor(shade1, lightText, this.#darkText)};
      --secondary-tint-1: ${tint1};
      --secondary-tint-1-text: ${this.getContrastColor(tint1, lightText, this.#darkText)};
    }`;

    const sheet = document.createElement('style');
    sheet.innerHTML = secondaryStyle;
    document.head.appendChild(sheet);
  }

  private colorMix(color1: string, color2: string, percent: number) {
    const red1 = parseInt(color1[1] + color1[2], 16);
    const green1 = parseInt(color1[3] + color1[4], 16);
    const blue1 = parseInt(color1[5] + color1[6], 16);

    const red2 = parseInt(color2[1] + color2[2], 16);
    const green2 = parseInt(color2[3] + color2[4], 16);
    const blue2 = parseInt(color2[5] + color2[6], 16);

    const red = Math.round(this.mix(red1, red2, percent));
    const green = Math.round(this.mix(green1, green2, percent));
    const blue = Math.round(this.mix(blue1, blue2, percent));

    return this.generateHex(red, green, blue);
  }

  private generateHex(r: number, g: number, b: number): string {
    let red = r.toString(16);
    let green = g.toString(16);
    let blue = b.toString(16);

    while (red.length < 2) { red = `0${red}`; }
    while (green.length < 2) { green = `0${green}`; }
    while (blue.length < 2) { blue = `0${blue}`; }

    return `#${red}${green}${blue}`;
  }

  private mix(start: number, end: number, percent: number): number {
    return start + percent * (end - start);
  }

  private getContrastColor(bgColor: string, lightColor: string, darkColor: string): string {
    const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
    const r = parseInt(color.substring(0, 2), 16); // hex to Red
    const g = parseInt(color.substring(2, 4), 16); // hex to Green
    const b = parseInt(color.substring(4, 6), 16); // hex to Blue
    const uicolors = [r / 255, g / 255, b / 255];
    const c = uicolors.map((col) => {
      if (col <= 0.03928) {
        return col / 12.92;
      }
      return Math.pow((col + 0.055) / 1.055, 2.4);
    });

    const l = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    return l > 0.179 ? darkColor : lightColor;
  }
}
